// The Cloudflare Turnstile challenge on sign-up (F8). Rendered only when the server gives a site key
// (GET /v1/pricing, `turnstile_site_key`); the token it hands out goes to POST /v1/signup. Explicit render: the
// script is loaded once, on first use. A token works once, so `resetKey` clears the widget after a refused sign-up.
import { useEffect, useRef } from "react";

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render(
    el: HTMLElement,
    o: {
      sitekey: string;
      theme?: "dark" | "light" | "auto";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ): string;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** Loads Cloudflare's script once; rejects when it cannot be loaded (offline, blocked). */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = SCRIPT;
    el.async = true;
    el.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile missing")));
    el.onerror = () => {
      loading = null;
      reject(new Error("turnstile blocked"));
    };
    document.head.append(el);
  });
  return loading;
}

export function Turnstile({
  siteKey,
  onToken,
  onUnavailable,
  resetKey = 0,
}: {
  siteKey: string;
  /** a token, or null once it expired or the challenge failed */
  onToken: (token: string | null) => void;
  /** the script could not be loaded: the page says so and sign-up waits */
  onUnavailable?: () => void;
  /** change it to start the challenge over (a token is used up by one sign-up) */
  resetKey?: number;
}) {
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const cb = useRef({ onToken, onUnavailable });
  cb.current = { onToken, onUnavailable };

  useEffect(() => {
    let live = true;
    loadTurnstile().then(
      (t) => {
        if (!live || !box.current) return;
        widget.current = t.render(box.current, {
          sitekey: siteKey,
          theme: "dark",
          callback: (token) => cb.current.onToken(token),
          "expired-callback": () => cb.current.onToken(null),
          "error-callback": () => cb.current.onToken(null),
        });
      },
      () => live && cb.current.onUnavailable?.(),
    );
    return () => {
      live = false;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new resetKey starts the challenge over
  useEffect(() => {
    if (widget.current && window.turnstile) {
      window.turnstile.reset(widget.current);
      cb.current.onToken(null);
    }
  }, [resetKey]);

  return <div ref={box} className="login-turnstile" />;
}
